import React, { useState, useEffect } from "react";
import {
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Icon,
  Input,
  SimpleGrid,
  Select,
  Stack,
  useToast,
} from "@chakra-ui/react";
import { EditIcon } from "@chakra-ui/icons";
import axios from "axios";
import {
  FaEdit,
  FaUser,
  FaMoneyBillAlt,
  FaEnvelope,
  FaHome,
  FaCalendarAlt,
  FaVenusMars,
  FaBook,
  FaTint,
  FaIdCard,
  FaMale,
  FaFemale,
  FaPhone,
} from "react-icons/fa";
import { BASE_URL } from "../../../../../Globalapi/Globalapi";

const EditBookModal = ({ issueId, handleSaveEdit }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState({
    name: "",
    issueDate: "",
    status: "",
    returnDate: "",
    fineAmount: "",
    studentName: "",
    dueDate: "", // Added dueDate to state
  });
  const toast = useToast();

  useEffect(() => {
    if (issueId && isModalOpen) {
      fetchBookIssue(issueId);
    }
  }, [issueId, isModalOpen]);

  const fetchBookIssue = async (issueId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/admin/library/issues/view_issue.php?issue_id=${issueId}`
      );
      console.log("view", response);

      if (response.data.success) {
        const { data } = response.data;

        setSelectedBook({
          name: data.title || "",
          issueDate: data.issue_date || "",
          status: data.status || "",
          returnDate: data.return_date || "",
          dueDate: data.due_date || "", // Set due date here
          fineAmount: data.is_fine ? "Yes" : "No",
          studentName: data.member_name || "",
        });
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error fetching book issue details:", error);
      toast({
        title: "Error",
        description: "Failed to fetch book issue details.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSelectedBook((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveChanges = async () => {
    try {
      const updatedData = {
        user_id: sessionStorage.getItem("user_id"),
        issue_id: issueId,
        due_date: selectedBook.dueDate, // Send dueDate
        fine_amount: selectedBook.fineAmount,
      };

      const response = await axios.put(
        `${BASE_URL}/admin/library/issues/edit_issue.php`,
        updatedData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      console.log("edit", response);

      if (response.data.success) {
        toast({
          title: "Success",
          description: "Changes saved successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        handleSaveEdit && handleSaveEdit(selectedBook);
        handleCloseModal();
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error saving changes:", error);
      toast({
        title: "Error",
        description: "Failed to save changes.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      {/* IconButton to open the modal */}
      <IconButton
        icon={<EditIcon />}
        colorScheme="blue"
        size="sm"
        onClick={handleOpenModal}
      />

      {/* Edit Modal */}
      <Modal
      isCentered
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        size="lg"
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent
          borderRadius="lg"
          boxShadow="xl"
          bg="linear-gradient(135deg, #e0f7f7, #f0ffff)"
          color="gray.700"
          p={6}
          maxW="700px"
          maxH="550px"
          overflowY="auto"
        >
          <ModalHeader
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
            color="teal.600"
          >
            <Icon as={FaEdit} boxSize={5} mr={2} />
            Edit Book Details
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            overflowY="auto"
            px={4}
            css={{
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(173, 216, 230, 0.6)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(173, 216, 230, 0.9)",
              },
            }}
          >
            {selectedBook ? (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {/* Student Name */}
                <FormControl>
                  <FormLabel>
                    <Icon as={FaUser} mr={1} color="teal.500" />
                    Student Name
                  </FormLabel>
                  <Input
                    bg="white"
                    _hover={{ borderColor: "teal.300" }}
                    _focus={{
                      borderColor: "teal.500",
                      boxShadow: "0 0 0 1px teal.500",
                    }}
                    name="studentName"
                    value={selectedBook.studentName}
                    isReadOnly
                    placeholder="Student Name"
                  />
                </FormControl>

                {/* Book Name */}
                <FormControl>
                  <FormLabel>
                    <Icon as={FaBook} mr={1} color="teal.500" />
                    Book Name
                  </FormLabel>
                  <Input
                    bg="white"
                    _hover={{ borderColor: "teal.300" }}
                    _focus={{
                      borderColor: "teal.500",
                      boxShadow: "0 0 0 1px teal.500",
                    }}
                    name="name"
                    value={selectedBook.name}
                    isReadOnly
                    placeholder="Book Name"
                  />
                </FormControl>

                {/* Issue Date */}
                <FormControl>
                  <FormLabel>
                    <Icon as={FaCalendarAlt} mr={1} color="teal.500" />
                    Issue Date
                  </FormLabel>
                  <Input
                    bg="white"
                    _hover={{ borderColor: "teal.300" }}
                    _focus={{
                      borderColor: "teal.500",
                      boxShadow: "0 0 0 1px teal.500",
                    }}
                    type="date"
                    name="issueDate"
                    value={selectedBook.issueDate}
                    onChange={handleChange}
                    placeholder="Select Issue Date"
                  />
                </FormControl>

                {/* Status */}
                <FormControl>
                  <FormLabel>
                    <Icon as={FaMoneyBillAlt} mr={1} color="teal.500" />
                    Status
                  </FormLabel>
                  <Select
                    bg="white"
                    _hover={{ borderColor: "teal.300" }}
                    _focus={{
                      borderColor: "teal.500",
                      boxShadow: "0 0 0 1px teal.500",
                    }}
                    name="status"
                    value={selectedBook.status}
                    onChange={handleChange}
                    placeholder="Select Status"
                  >
                    <option value="Issued">Issued</option>
                    <option value="Returned">Returned</option>
                    <option value="Overdue">Overdue</option>
                  </Select>
                </FormControl>

                {/* Return Date */}
                <FormControl>
                  <FormLabel>
                    <Icon as={FaCalendarAlt} mr={1} color="teal.500" />
                    Return Date
                  </FormLabel>
                  <Input
                    bg="white"
                    _hover={{ borderColor: "teal.300" }}
                    _focus={{
                      borderColor: "teal.500",
                      boxShadow: "0 0 0 1px teal.500",
                    }}
                    type="date"
                    name="returnDate"
                    value={selectedBook.returnDate}
                    onChange={handleChange}
                    placeholder="Select Return Date"
                  />
                </FormControl>

                {/* Due Date (new field) */}
                <FormControl>
                  <FormLabel>
                    <Icon as={FaCalendarAlt} mr={1} color="teal.500" />
                    Due Date
                  </FormLabel>
                  <Input
                    type="date"
                    name="dueDate"
                    bg="white"
                    _hover={{ borderColor: "teal.300" }}
                    _focus={{
                      borderColor: "teal.500",
                      boxShadow: "0 0 0 1px teal.500",
                    }}
                    value={selectedBook.dueDate}
                    onChange={handleChange}
                    placeholder="Select Due Date"
                  />
                </FormControl>

                {/* Fine Amount */}
                <FormControl>
                  <FormLabel>
                    <Icon as={FaMoneyBillAlt} mr={1} color="teal.500" />
                    Fine Amount
                  </FormLabel>
                  <Input
                    bg="white"
                    _hover={{ borderColor: "teal.300" }}
                    _focus={{
                      borderColor: "teal.500",
                      boxShadow: "0 0 0 1px teal.500",
                    }}
                    name="fineAmount"
                    value={selectedBook.fineAmount}
                    onChange={handleChange}
                    placeholder="Enter Fine Amount"
                  />
                </FormControl>
              </SimpleGrid>
            ) : (
              <p>Loading book details...</p>
            )}
          </ModalBody>

          <ModalFooter>
            {/* <Button variant="ghost" onClick={handleCloseModal}>
              Cancel
            </Button> */}
            <Button
              bgGradient="linear(to-r, teal.400, teal.600)"
              color="white"
              _hover={{ bgGradient: "linear(to-r, teal.500, teal.700)" }}
              onClick={handleSaveChanges}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default EditBookModal;
